<template>
    <div class="file-preview-modal" v-if="visible" @click.self="handleClose">
        <div class="preview-container">
            <!-- Header -->
            <div class="preview-header">
                <div class="file-info">
                    <span class="file-name">{{ currentFile.name }}</span>
                    <span class="file-counter" v-if="files.length > 1">
                        {{ currentIndex + 1 }} / {{ files.length }}
                    </span>
                </div>
                <div class="header-actions">
                    <a-tooltip :title="$t('googleDrive.download')">
                        <a-button type="link" class="action-btn" @click="handleDownload" :loading="downloading">
                            <a-icon type="download" />
                        </a-button>
                    </a-tooltip>
                    <a-tooltip :title="$t('googleDrive.openInNewTab')">
                        <a-button type="link" class="action-btn" @click="handleOpenInNewTab">
                            <a-icon type="export" />
                        </a-button>
                    </a-tooltip>
                    <a-button type="link" class="action-btn close-btn" @click="handleClose">
                        <a-icon type="close" />
                    </a-button>
                </div>
            </div>

            <!-- Navigation Arrows -->
            <button
                class="nav-arrow nav-prev"
                v-if="files.length > 1"
                @click="goToPrev"
                :disabled="currentIndex === 0"
                type="button"
                :aria-label="$t('googleDrive.previewPreviousFile')"
            >
                <a-icon type="left" />
            </button>
            <button
                class="nav-arrow nav-next"
                v-if="files.length > 1"
                @click="goToNext"
                :disabled="currentIndex === files.length - 1"
                type="button"
                :aria-label="$t('googleDrive.previewNextFile')"
            >
                <a-icon type="right" />
            </button>

            <!-- Preview Content -->
            <div class="preview-content">
                <!-- Loading State -->
                <div class="loading-state" v-if="loading">
                    <a-spin size="large" />
                </div>

                <!-- Image Preview -->
                <div class="image-preview" v-else-if="isImage">
                    <img :src="previewUrl" :alt="currentFile.name" @load="onImageLoad" @error="onLoadError" />
                </div>

                <!-- Video Preview -->
                <div class="video-preview" v-else-if="isVideo">
                    <iframe
                        :src="googleDriveEmbedUrl"
                        frameborder="0"
                        allowfullscreen
                        allow="autoplay"
                    ></iframe>
                </div>

                <!-- PDF Preview -->
                <div class="pdf-preview" v-else-if="isPdf">
                    <iframe
                        :src="googleDriveEmbedUrl"
                        frameborder="0"
                    ></iframe>
                </div>

                <!-- Google Docs/Sheets/Slides Preview -->
                <div class="google-docs-preview" v-else-if="isGoogleDoc">
                    <iframe
                        :src="googleDocsPreviewUrl"
                        frameborder="0"
                    ></iframe>
                </div>

                <!-- Unsupported File Type - File Card -->
                <div class="file-card-preview" v-else>
                    <div class="file-card-content">
                        <div class="file-icon-large">
                            <a-icon :type="getFileIcon(currentFile.mimeType)" />
                        </div>
                        <h3 class="file-card-name">{{ currentFile.name }}</h3>
                        <div class="file-card-details">
                            <p v-if="currentFile.size">
                                <strong>{{ $t('googleDrive.fileSize') }}:</strong>
                                {{ formatBytes(currentFile.size) }}
                            </p>
                            <p v-if="currentFile.mimeType">
                                <strong>{{ $t('googleDrive.fileType') }}:</strong>
                                {{ getReadableMimeType(currentFile.mimeType) }}
                            </p>
                            <p v-if="currentFile.modifiedTime">
                                <strong>{{ $t('googleDrive.modifiedTime') }}:</strong>
                                {{ formatDate(currentFile.modifiedTime) }}
                            </p>
                            <p v-if="currentFile.owners && currentFile.owners.length">
                                <strong>{{ $t('googleDrive.owner') }}:</strong>
                                {{ currentFile.owners.map(o => o.displayName || o.emailAddress).join(', ') }}
                            </p>
                        </div>
                        <p class="file-card-message">
                            {{ $t('googleDrive.previewNotAvailable') }}
                        </p>
                        <div class="file-card-actions">
                            <a-button type="primary" @click="handleDownload" :loading="downloading">
                                <a-icon type="download" />
                                {{ $t('googleDrive.downloadToSupsis') }}
                            </a-button>
                            <a-button @click="handleOpenInNewTab">
                                <a-icon type="export" />
                                {{ $t('googleDrive.openInGoogleDrive') }}
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- File Thumbnails Strip -->
            <div class="thumbnails-strip" v-if="files.length > 1">
                <div
                    class="thumbnail-item"
                    :class="{ active: index === currentIndex }"
                    v-for="(file, index) in files"
                    :key="file.id"
                    @click="goToIndex(index)"
                >
                    <img v-if="file.thumbnailLink" :src="file.thumbnailLink" :alt="file.name" />
                    <a-icon v-else :type="getFileIcon(file.mimeType)" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import { GOOGLE_DRIVE_API } from "../../api/google-drive";

export default {
    name: "FilePreviewModal",
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        files: {
            type: Array,
            default: function() {
                return [];
            },
        },
        initialIndex: {
            type: Number,
            default: 0,
        },
        channelId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            currentIndex: 0,
            loading: false,
            downloading: false,
            loadError: false,
        };
    },
    computed: {
        currentFile() {
            return this.files[this.currentIndex] || {};
        },
        isImage() {
            var mimeType = this.currentFile.mimeType || "";
            return mimeType.startsWith("image/");
        },
        isVideo() {
            var mimeType = this.currentFile.mimeType || "";
            return mimeType.startsWith("video/");
        },
        isPdf() {
            var mimeType = this.currentFile.mimeType || "";
            return mimeType === "application/pdf";
        },
        isGoogleDoc() {
            var mimeType = this.currentFile.mimeType || "";
            return mimeType.startsWith("application/vnd.google-apps.");
        },
        previewUrl() {
            if (!this.currentFile.id) return "";
            // Google Drive thumbnail API ile büyük resim al
            return "https://drive.google.com/thumbnail?id=" + this.currentFile.id + "&sz=w1000";
        },
        googleDriveEmbedUrl() {
            if (!this.currentFile.id) return "";
            return "https://drive.google.com/file/d/" + this.currentFile.id + "/preview";
        },
        googleDocsPreviewUrl() {
            if (!this.currentFile.webViewLink) return this.googleDriveEmbedUrl;
            // Google Docs için preview URL
            var webViewLink = this.currentFile.webViewLink;
            return webViewLink.replace("/edit", "/preview").replace("/view", "/preview");
        },
    },
    methods: {
        handleClose() {
            this.$emit("close");
        },
        goToPrev() {
            if (this.currentIndex > 0) {
                this.currentIndex--;
                this.loadError = false;
            }
        },
        goToNext() {
            if (this.currentIndex < this.files.length - 1) {
                this.currentIndex++;
                this.loadError = false;
            }
        },
        goToIndex(index) {
            if (index >= 0 && index < this.files.length) {
                this.currentIndex = index;
                this.loadError = false;
            }
        },
        async handleDownload() {
            if (!this.currentFile.id || !this.channelId) return;
            this.downloading = true;
            try {
                var response = await GOOGLE_DRIVE_API.downloadFile(this.channelId, this.currentFile.id);
                if (response.data.asset) {
                    this.$message.success(this.$t("googleDrive.fileDownloaded"));
                    this.$emit("downloaded", response.data.asset);
                }
            } catch (error) {
                this.$message.error(this.$t("googleDrive.errorDownloading"));
                console.error("Download error:", error);
            } finally {
                this.downloading = false;
            }
        },
        handleOpenInNewTab() {
            var url = this.currentFile.webViewLink;
            if (!url) {
                url = "https://drive.google.com/file/d/" + this.currentFile.id + "/view";
            }
            window.open(url, "_blank");
        },
        onImageLoad() {
            this.loading = false;
            this.loadError = false;
        },
        onLoadError() {
            this.loadError = true;
            this.loading = false;
        },
        handleKeydown(event) {
            if (!this.visible) return;
            if (event.key === "Escape") {
                event.stopPropagation();
                event.preventDefault();
                this.handleClose();
            } else if (event.key === "ArrowLeft") {
                event.stopPropagation();
                this.goToPrev();
            } else if (event.key === "ArrowRight") {
                event.stopPropagation();
                this.goToNext();
            }
        },
        getFileIcon(mimeType) {
            if (!mimeType) return "file";
            if (mimeType.indexOf("image") !== -1) return "file-image";
            if (mimeType.indexOf("video") !== -1) return "video-camera";
            if (mimeType.indexOf("audio") !== -1) return "sound";
            if (mimeType.indexOf("pdf") !== -1) return "file-pdf";
            if (mimeType.indexOf("spreadsheet") !== -1 || mimeType.indexOf("excel") !== -1) return "file-excel";
            if (mimeType.indexOf("document") !== -1 || mimeType.indexOf("word") !== -1) return "file-word";
            if (mimeType.indexOf("presentation") !== -1 || mimeType.indexOf("powerpoint") !== -1) return "file-ppt";
            if (mimeType.indexOf("zip") !== -1 || mimeType.indexOf("compressed") !== -1) return "file-zip";
            return "file";
        },
        getReadableMimeType(mimeType) {
            if (!mimeType) return "Bilinmiyor";
            var mimeMap = {
                "application/pdf": "PDF Belgesi",
                "application/vnd.google-apps.document": "Google Doküman",
                "application/vnd.google-apps.spreadsheet": "Google E-Tablo",
                "application/vnd.google-apps.presentation": "Google Slayt",
                "application/vnd.google-apps.folder": "Klasör",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word Belgesi",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel Tablosu",
                "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PowerPoint Sunusu",
                "application/zip": "ZIP Arşivi",
                "application/x-rar-compressed": "RAR Arşivi",
                "text/plain": "Metin Dosyası",
                "text/html": "HTML Dosyası",
                "text/css": "CSS Dosyası",
                "text/javascript": "JavaScript Dosyası",
                "application/json": "JSON Dosyası",
            };
            if (mimeMap[mimeType]) return mimeMap[mimeType];
            if (mimeType.indexOf("image/") === 0) return "Resim (" + mimeType.split("/")[1].toUpperCase() + ")";
            if (mimeType.indexOf("video/") === 0) return "Video (" + mimeType.split("/")[1].toUpperCase() + ")";
            if (mimeType.indexOf("audio/") === 0) return "Ses (" + mimeType.split("/")[1].toUpperCase() + ")";
            return mimeType;
        },
        formatBytes(bytes) {
            if (!bytes) return "0 B";
            var k = 1024;
            var sizes = ["B", "KB", "MB", "GB", "TB"];
            var i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
    },
    watch: {
        visible: function(val) {
            if (val) {
                this.currentIndex = this.initialIndex;
                this.loadError = false;
                // capture: true ile event'i önce yakala, böylece diğer modallara ulaşmasın
                document.addEventListener("keydown", this.handleKeydown, true);
                document.body.style.overflow = "hidden";
            } else {
                document.removeEventListener("keydown", this.handleKeydown, true);
                document.body.style.overflow = "";
            }
        },
        initialIndex: function(val) {
            this.currentIndex = val;
        },
    },
    mounted() {
        if (this.visible) {
            document.addEventListener("keydown", this.handleKeydown, true);
        }
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleKeydown, true);
        document.body.style.overflow = "";
    },
};
</script>

<style lang="scss" scoped>
.file-preview-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.92);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.2s ease;

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
}

.preview-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.preview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    z-index: 10;

    .file-info {
        display: flex;
        align-items: center;
        gap: 12px;

        .file-name {
            font-size: 16px;
            font-weight: 500;
            max-width: 400px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .file-counter {
            font-size: 14px;
            color: rgba(255, 255, 255, 0.7);
            background: rgba(255, 255, 255, 0.1);
            padding: 4px 10px;
            border-radius: 12px;
        }
    }

    .header-actions {
        display: flex;
        align-items: center;
        gap: 8px;

        .action-btn {
            color: white;
            font-size: 18px;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: all 0.2s;

            &:hover {
                background: rgba(255, 255, 255, 0.1);
            }
        }

        .close-btn {
            font-size: 20px;
        }
    }
}

.nav-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 50px;
    height: 50px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    color: white;
    font-size: 20px;
    cursor: pointer;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.2);
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }

    &.nav-prev {
        left: 20px;
    }

    &.nav-next {
        right: 20px;
    }
}

.preview-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 20px 80px;

    .loading-state {
        color: white;
    }

    .image-preview {
        max-width: 100%;
        max-height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            max-width: 100%;
            max-height: calc(100vh - 180px);
            object-fit: contain;
            border-radius: 4px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }
    }

    .video-preview,
    .pdf-preview,
    .google-docs-preview {
        width: 100%;
        height: 100%;
        max-width: 1200px;
        max-height: calc(100vh - 180px);

        iframe {
            width: 100%;
            height: 100%;
            border-radius: 8px;
            background: white;
        }
    }

    .file-card-preview {
        display: flex;
        align-items: center;
        justify-content: center;

        .file-card-content {
            background: var(--color-bg-default, #fff);
            border-radius: 12px;
            padding: 40px;
            text-align: center;
            max-width: 450px;
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);

            .file-icon-large {
                font-size: 72px;
                color: #5f6368;
                margin-bottom: 20px;
            }

            .file-card-name {
                font-size: 18px;
                font-weight: 600;
                margin-bottom: 20px;
                word-break: break-word;
                color: var(--color-text-primary, #333);
            }

            .file-card-details {
                text-align: left;
                margin-bottom: 20px;
                padding: 15px;
                background: var(--color-bg-secondary, #f5f5f5);
                border-radius: 8px;

                p {
                    margin: 8px 0;
                    font-size: 13px;
                    color: var(--color-text-secondary, #666);

                    strong {
                        color: var(--color-text-primary, #333);
                    }
                }
            }

            .file-card-message {
                color: var(--color-text-secondary, #888);
                font-size: 14px;
                margin-bottom: 20px;
            }

            .file-card-actions {
                display: flex;
                gap: 12px;
                justify-content: center;
            }
        }
    }
}

.thumbnails-strip {
    display: flex;
    gap: 8px;
    padding: 12px 20px;
    background: rgba(0, 0, 0, 0.5);
    overflow-x: auto;
    justify-content: center;

    .thumbnail-item {
        width: 60px;
        height: 60px;
        flex-shrink: 0;
        border-radius: 6px;
        overflow: hidden;
        cursor: pointer;
        border: 2px solid transparent;
        background: rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;

        &:hover {
            border-color: rgba(255, 255, 255, 0.5);
        }

        &.active {
            border-color: #4285f4;
        }

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .anticon {
            font-size: 24px;
            color: rgba(255, 255, 255, 0.7);
        }
    }
}
</style>
